<script setup lang="ts">
import { watch } from 'vue'
import { NButton, NEmpty, NSpin } from 'naive-ui'
import type { AgentChannelSummary } from '../../stores/agent_channels'
import { formatDate } from '../../composables/useFormattedDate'
import { useNow } from '../../composables/useNow'

const props = defineProps<{
  channels: AgentChannelSummary[]
  selectedId: number | null
  loading: boolean
  loadFailed?: boolean
}>()
const emit = defineEmits<{ select: [id: number]; new: [] }>()

const now = useNow()
watch(
  () => props.channels,
  () => (now.value = new Date()),
  { deep: true },
)

function memberLabel(count: number): string {
  return `${count} member${count === 1 ? '' : 's'}`
}
</script>

<template>
  <nav aria-label="Channels" class="flex flex-col gap-2">
    <div class="flex items-center justify-between gap-2">
      <h2 class="text-sm font-semibold text-white/80 m-0">Channels</h2>
      <NButton size="tiny" tertiary data-testid="channel-new" @click="emit('new')">+ New</NButton>
    </div>

    <div v-if="loading" class="flex justify-center py-4"><NSpin size="small" /></div>

    <p v-else-if="loadFailed" class="text-xs text-white/60 m-0" data-testid="channels-list-error">
      Couldn't load channels.
    </p>

    <NEmpty
      v-else-if="channels.length === 0"
      size="small"
      description="No channels yet. Channels are rooms where agent threads talk to each other."
      class="py-2"
      data-testid="channels-empty"
    >
      <template #extra>
        <NButton size="small" data-testid="channels-empty-create" @click="emit('new')">Create channel</NButton>
      </template>
    </NEmpty>

    <ul v-else class="m-0 p-0 list-none flex flex-col gap-1 overflow-y-auto" data-testid="channel-list">
      <li v-for="channel in channels" :key="channel.id">
        <button
          type="button"
          class="w-full text-left px-2 py-2 rounded border"
          :class="
            channel.id === selectedId
              ? 'bg-brand-primary/10 border-brand-primary/30 text-white'
              : 'border-transparent hover:bg-white/10 text-white/70'
          "
          :aria-current="channel.id === selectedId ? 'true' : undefined"
          :data-testid="`channel-${channel.id}`"
          @click="emit('select', channel.id)"
        >
          <span class="flex items-center justify-between gap-2 min-w-0">
            <span class="text-sm truncate">#{{ channel.name }}</span>
            <slot name="unread" :channel="channel" />
          </span>
          <span class="flex items-center justify-between gap-2 text-xs text-white/60">
            <span>{{ memberLabel(channel.member_count) }}</span>
            <span>{{ channel.last_activity_at ? formatDate(channel.last_activity_at, now) : 'No activity' }}</span>
          </span>
        </button>
      </li>
    </ul>
  </nav>
</template>
